import { Link } from 'react-router-dom';
import LegalLayout from '../components/LegalLayout';
import { COMPANY, DPA_CONTACT, HOSTING, LEGAL_UPDATED_TERMS } from '../constants/legal';

const PrivacyPage = () => (
  <LegalLayout
    testId="privacy-page"
    title="Privacy Policy — Politique de confidentialité (RGPD)"
    description="How Gate 33 (NPDS) collects, uses and protects personal data — GDPR compliant privacy policy."
    path="/legal/privacy"
    updated={LEGAL_UPDATED_TERMS}
  >
    <p className="text-lg text-[#1A1A1A] leading-relaxed mb-8">
      This Privacy Policy explains how {COMPANY.legalName} (trading as {COMPANY.tradingName}) collects, uses and protects personal data of visitors and clients of gate33.co, in compliance with the EU General Data Protection Regulation (GDPR, Regulation 2016/679) and the French Data Protection Act (Loi Informatique et Libertés).
    </p>

    <h2>1. Data controller</h2>
    <p>
      {COMPANY.legalName}. SIREN {COMPANY.siren}. Contact for privacy matters: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
    </p>

    <h2>2. Categories of personal data collected</h2>
    <ul>
      <li><strong>Identification data:</strong> first name, last name, email, position/job title, company (Newsletter, Discovery Call, Enrollment); postal address (training contract).</li>
      <li><strong>Application data:</strong> CV, LinkedIn profile URL, professional background (Mentorship intake, Operational Assessment).</li>
      <li><strong>Payment data:</strong> processed exclusively by Stripe/PayPal. {COMPANY.legalName} never stores card details.</li>
      <li><strong>Technical data:</strong> IP address, browser, device, pages visited, time spent (via analytics — only with consent).</li>
      <li><strong>Communications:</strong> content of your emails, messages via HubSpot Chat, notes taken during discovery calls.</li>
      <li><strong>Call recordings and notes:</strong> Mentorship sessions, unless refused by the Client.</li>
    </ul>

    <h2>3. Purposes and legal bases</h2>
    <table>
      <thead>
        <tr>
          <th>Purpose</th>
          <th>Legal basis (GDPR Art. 6)</th>
          <th>Retention</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Managing enrollment, delivering the training, invoicing</td>
          <td>Performance of a contract (Art. 6.1.b)</td>
          <td>10 years (accounting obligations)</td>
        </tr>
        <tr>
          <td>Mentorship delivery: CV rebuild, applications sent on the Client's behalf, call recordings</td>
          <td>Performance of a contract (Art. 6.1.b)</td>
          <td>CVs and application data: 3 years after the end of the Mentorship; call recordings: until the end of the programme plus 3 months</td>
        </tr>
        <tr>
          <td>Discovery call and application data of non-clients (CV, LinkedIn)</td>
          <td>Pre-contractual measures (Art. 6.1.b)</td>
          <td>3 years from last contact</td>
        </tr>
        <tr>
          <td>Sending the newsletter and marketing communications</td>
          <td>Consent (Art. 6.1.a)</td>
          <td>Until unsubscription, then 3 years max</td>
        </tr>
        <tr>
          <td>Responding to enquiries and discovery calls</td>
          <td>Pre-contractual measures (Art. 6.1.b)</td>
          <td>3 years from last contact</td>
        </tr>
        <tr>
          <td>Website analytics (audience measurement)</td>
          <td>Consent (Art. 6.1.a) — via cookie banner</td>
          <td>13 months</td>
        </tr>
        <tr>
          <td>Fraud prevention, security, legal obligations</td>
          <td>Legitimate interest / legal obligation (Art. 6.1.f, 6.1.c)</td>
          <td>As required by law</td>
        </tr>
      </tbody>
    </table>

    <h2>4. Data recipients (subprocessors)</h2>
    <p>
      Personal data is processed by the following subprocessors, each bound by data processing agreements:
    </p>
    <ul>
      <li><strong>HubSpot, Inc.</strong> (USA) — CRM, contact management, newsletter list, chat widget. Transfers based on EU Standard Contractual Clauses.</li>
      <li><strong>Stripe Payments Europe, Ltd.</strong> (Ireland/USA) — payment processing.</li>
      <li><strong>PayPal (Europe) S.à r.l. et Cie, S.C.A.</strong> (Luxembourg) — instalment payments (up to 24×).</li>
      <li><strong>Resend, Inc.</strong> (USA) — transactional email delivery.</li>
      <li><strong>Google (Google Analytics 4)</strong> (USA) — anonymised audience analytics, only if consent given.</li>
      <li><strong>LinkedIn Corporation</strong> (USA) — LinkedIn Insight Tag, only if consent given.</li>
      <li><strong>Meta Platforms, Inc.</strong> (USA) — Meta Pixel advertising conversion tracking, only if consent given.</li>
      <li><strong>Calendly, Inc.</strong> (USA) — discovery call scheduling.</li>
      <li><strong>Fathom Video, Inc.</strong> (USA) — recording and transcription of calls.</li>
      <li><strong>Google LLC / Google Ireland Ltd</strong> — Google Meet video calls and Gmail accounts used for applications.</li>
      <li><strong>WhatsApp Ireland Ltd (Meta)</strong> — client messaging support.</li>
      <li><strong>Klarna Bank AB</strong> (Sweden) — instalment payments.</li>
      <li><strong>Anthropic PBC</strong> (USA) — AI assistance used to analyse CVs and draft documents.</li>
      <li><strong>{HOSTING.name}</strong> ({HOSTING.address}) — website hosting.</li>
    </ul>

    <h2>5. Data transfers outside the EEA</h2>
    <p>
      Certain subprocessors (Stripe, HubSpot, Google, LinkedIn, Calendly, Fathom, Anthropic, Meta (WhatsApp)) may process data outside the European Economic Area, in particular in the United States. Such transfers are secured by the EU Standard Contractual Clauses adopted by the European Commission and, where applicable, by adherence to the EU-US Data Privacy Framework.
    </p>

    <h2>6. Your rights under GDPR</h2>
    <p>You have the following rights regarding your personal data:</p>
    <ul>
      <li><strong>Right of access</strong> (Art. 15)</li>
      <li><strong>Right to rectification</strong> (Art. 16)</li>
      <li><strong>Right to erasure / to be forgotten</strong> (Art. 17)</li>
      <li><strong>Right to restriction of processing</strong> (Art. 18)</li>
      <li><strong>Right to data portability</strong> (Art. 20)</li>
      <li><strong>Right to object</strong> to processing (Art. 21), including for direct marketing purposes at any time</li>
      <li><strong>Right to withdraw consent</strong> at any time where processing is based on consent</li>
      <li><strong>Right to define post-mortem directives</strong> regarding your personal data (French Data Protection Act, Article 85)</li>
    </ul>
    <p>
      To exercise any of these rights, please contact us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. We will respond within one month.
    </p>

    <h2>7. Right to lodge a complaint</h2>
    <p>
      If you believe your rights have not been respected, you may lodge a complaint with the French Data Protection Authority ({DPA_CONTACT.authority}):
    </p>
    <ul>
      <li><a href={DPA_CONTACT.authorityWebsite} target="_blank" rel="noopener noreferrer">{DPA_CONTACT.authorityWebsite}</a></li>
    </ul>

    <h2>8. Cookies</h2>
    <p>
      Cookies are governed by our <Link to="/legal/cookies">Cookie Policy</Link>. Non-essential cookies (analytics, marketing) are disabled by default and only loaded after your explicit consent through the cookie banner.
    </p>

    <h2>9. Security</h2>
    <p>
      {COMPANY.legalName} implements appropriate technical and organisational measures to protect personal data, including HTTPS encryption, access controls, and contractual safeguards with subprocessors. However, no method of transmission over the internet is 100% secure.
    </p>

    <h2>10. Changes to this policy</h2>
    <p>
      This Privacy Policy may be updated to reflect changes in the law or in our practices. The most recent version is always available at gate33.co/legal/privacy.
    </p>
  </LegalLayout>
);

export default PrivacyPage;
